import { NavLink } from 'react-router-dom';
import { hasRight, useAuthStore } from '../auth/store';
import { useTelegramPendingCount } from '../api/hooks';

const NAV = [
  { to: '/dashboard', label: 'Дашборд', icon: '◧' },
  { to: '/orders', label: 'Заявки', icon: '☰' },
  { to: '/balances', label: 'Балансы', icon: '∑' },
  { to: '/transactions', label: 'Транзакции', icon: '⇄' },
];

const linkBase =
  'flex h-9 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors duration-150 ease-out';

/**
 * На десктопе (≥ md) — статичная колонка. На мобильном — выдвижное меню:
 * `open`/`onClose` управляет AppLayout (кнопка ☰ в верхней панели).
 */
export function Sidebar({ open = false, onClose }: { open?: boolean; onClose?: () => void }) {
  const user = useAuthStore((s) => s.user);
  const rights = useAuthStore((s) => s.rights);
  const clear = useAuthStore((s) => s.clear);
  // «Чаты» (контрагенты /admin/chats) — по accounts.manage (оператор/менеджер/админ).
  // «Курсы» и «Пользователи» — только админ (users.manage).
  const showChats = hasRight(rights, 'accounts.manage');
  const showAdminExtra = hasRight(rights, 'users.manage');
  const showApi = hasRight(rights, 'integrations.manage');
  const showAdmin = showChats || showAdminExtra || showApi;
  // «Настройка» листенера — управление чатами/правилами; «События» — apply-events.
  const showTelegramConfig =
    hasRight(rights, 'telegram.manage') || hasRight(rights, 'telegram.manage-chats');
  const showTelegramEvents =
    hasRight(rights, 'telegram.apply-events') || hasRight(rights, 'telegram.manage');
  const showTelegram = showTelegramConfig || showTelegramEvents;
  const showAudit = hasRight(rights, 'audit.view');
  const { data: pendingCount } = useTelegramPendingCount();
  const pending = pendingCount?.count ?? 0;

  return (
    <>
    {open && (
      <div
        className="fixed inset-0 z-30 md:hidden"
        style={{ background: 'var(--bg-overlay)' }}
        onClick={onClose}
        aria-hidden="true"
      />
    )}
    <aside
      className={`fixed inset-y-0 left-0 z-40 flex h-full w-64 shrink-0 flex-col border-r border-border bg-bg2 transition-transform duration-200 ease-out md:static md:z-auto md:w-56 md:translate-x-0 md:shadow-none ${
        open ? 'translate-x-0 shadow-lg' : '-translate-x-full'
      }`}
    >
      <div className="flex items-start justify-between border-b border-border-subtle px-4 py-4">
        <div>
          <div className="text-base font-semibold tracking-tight text-text">Wave Panel</div>
          <div className="mt-0.5 text-xs text-text2">Учёт заявок</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть меню"
          className="-mr-1 -mt-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-xl leading-none text-text2 transition-colors duration-150 hover:bg-bg3 hover:text-text md:hidden"
        >
          ×
        </button>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            className={({ isActive }) =>
              `${linkBase} ${
                isActive
                  ? 'bg-[var(--accent-bg)] text-accent font-semibold'
                  : 'text-text2 hover:bg-bg3 hover:text-text'
              }`
            }
          >
            <span className="w-4 text-center font-mono text-base leading-none">{n.icon}</span>
            <span>{n.label}</span>
          </NavLink>
        ))}
        {showAudit && (
          <NavLink
            to="/audit"
            className={({ isActive }) =>
              `${linkBase} ${
                isActive
                  ? 'bg-[var(--accent-bg)] text-accent font-semibold'
                  : 'text-text2 hover:bg-bg3 hover:text-text'
              }`
            }
          >
            <span className="w-4 text-center font-mono text-base leading-none">⚖</span>
            <span>Аудит</span>
          </NavLink>
        )}
        {showAdmin && (
          <>
            <div className="my-2 border-t border-border-subtle" />
            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-subtle">
              Администрирование
            </div>
            {showChats && (
              <NavLink
                to="/admin/chats"
                className={({ isActive }) =>
                  `${linkBase} ${
                    isActive
                      ? 'bg-[var(--accent-bg)] text-accent font-semibold'
                      : 'text-text2 hover:bg-bg3 hover:text-text'
                  }`
                }
              >
                <span className="w-4 text-center font-mono text-base leading-none">💬</span>
                <span>Чаты</span>
              </NavLink>
            )}
            {showAdminExtra && (
              <>
                <NavLink
                  to="/admin/rates"
                  className={({ isActive }) =>
                    `${linkBase} ${
                      isActive
                        ? 'bg-[var(--accent-bg)] text-accent font-semibold'
                        : 'text-text2 hover:bg-bg3 hover:text-text'
                    }`
                  }
                >
                  <span className="w-4 text-center font-mono text-base leading-none">💱</span>
                  <span>Курсы</span>
                </NavLink>
                <NavLink
                  to="/admin/users"
                  className={({ isActive }) =>
                    `${linkBase} ${
                      isActive
                        ? 'bg-[var(--accent-bg)] text-accent font-semibold'
                        : 'text-text2 hover:bg-bg3 hover:text-text'
                    }`
                  }
                >
                  <span className="w-4 text-center font-mono text-base leading-none">⚙</span>
                  <span>Пользователи</span>
                </NavLink>
              </>
            )}
            {showApi && (
              <NavLink
                to="/admin/api"
                className={({ isActive }) =>
                  `${linkBase} ${
                    isActive
                      ? 'bg-[var(--accent-bg)] text-accent font-semibold'
                      : 'text-text2 hover:bg-bg3 hover:text-text'
                  }`
                }
              >
                <span className="w-4 text-center font-mono text-base leading-none">⇄</span>
                <span>API</span>
              </NavLink>
            )}
          </>
        )}
        {showTelegram && (
          <>
            <div className="my-2 border-t border-border-subtle" />
            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-subtle">
              Telegram-listener
            </div>
            {showTelegramConfig && (
              <NavLink
                to="/admin/telegram"
                end
                className={({ isActive }) =>
                  `${linkBase} ${
                    isActive
                      ? 'bg-[var(--accent-bg)] text-accent font-semibold'
                      : 'text-text2 hover:bg-bg3 hover:text-text'
                  }`
                }
              >
                <span className="w-4 text-center font-mono text-base leading-none">⌨</span>
                <span>Настройка</span>
              </NavLink>
            )}
            {showTelegramEvents && (
              <NavLink
                to="/admin/telegram/events"
                className={({ isActive }) =>
                  `${linkBase} ${
                    isActive
                      ? 'bg-[var(--accent-bg)] text-accent font-semibold'
                      : 'text-text2 hover:bg-bg3 hover:text-text'
                  }`
                }
              >
                <span className="w-4 text-center font-mono text-base leading-none">🔔</span>
                <span className="flex-1">События</span>
                {pending > 0 && (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[var(--accent-bg)] px-1.5 text-[10px] font-bold text-accent">
                    {pending}
                  </span>
                )}
              </NavLink>
            )}
          </>
        )}
      </nav>
      <div className="border-t border-border-subtle px-4 py-3">
        <div className="truncate text-sm font-medium text-text">{user?.displayName ?? '—'}</div>
        <div className="truncate text-xs text-text2">{user?.email}</div>
        <div className="mt-2 flex items-center gap-3">
          <NavLink
            to="/security"
            className={({ isActive }) =>
              `text-xs font-medium underline-offset-4 transition-colors duration-150 hover:underline ${
                isActive ? 'text-accent' : 'text-text2 hover:text-text'
              }`
            }
          >
            🔒 Безопасность
          </NavLink>
          <button
            onClick={() => clear()}
            className="text-xs font-medium text-text2 underline-offset-4 transition-colors duration-150 hover:text-text hover:underline"
          >
            Выйти
          </button>
        </div>
      </div>
    </aside>
    </>
  );
}
